<template>
    <div>
        <limitless-page-header :loading="busy_loading" :title="'Kodtyp'" :sub-title="title" :title-id="title_id">
            <template #header-elements>
                <limitless-page-header-button :route="{ name: 'productcodetypes.list' }" icon="icon-circle-left2">Tillbaka</limitless-page-header-button>
            </template>
        </limitless-page-header>

        <div v-if="!initialized && busy_loading" class="content">
            <i class="icon-spinner2 spinner"></i>
        </div>
        <div v-else-if="!not_found && code_type" class="content">
            <ValidationObserver ref="codetypeform">
                <form @submit.prevent="saveCodeType">
                    <div class="card">
                        <div class="card-body">
                            <div class="row">
                                <div class="col-lg-4">
                                    <div class="form-group">
                                        <label>Namn</label>
                                        <input type="text" class="form-control" :value="code_type.ProductCodeTypeName" disabled>
                                    </div>
                                </div>
                                <div class="col-lg-4">
                                    <div class="form-group">
                                        <label>Typ</label>
                                        <input type="text" class="form-control" :value="code_type.ProductCodeType" disabled>
                                    </div>
                                </div>
                                <div class="col-lg-4">
                                    <div class="form-group">
                                        <label>Status</label>
                                        <div>
                                            <span v-if="code_type.IsActive" class="badge badge-success">Aktiv</span>
                                            <span v-else class="badge badge-light">Inaktiv</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <limitless-tabs :tabs="language_tabs" class="mb-3">
                                <template v-for="lang in language_tabs" v-slot:[lang.slot]>
                                    <div :key="'tab-' + lang.id">
                                        <ValidationProvider :vid="'translations.' + lang.id + '.delivery_info'" name="Leveransinfo" v-slot="{ errors }">
                                            <div class="form-group">
                                                <label>Leveransinfo</label>
                                                <simple-text-editor
                                                    v-model="code_type.translations[lang.id].delivery_info"
                                                    :toolbar="[
                                                        'h2',
                                                        'h3',
                                                        'bold',
                                                        'italic',
                                                        'strike',
                                                        'underline',
                                                        'textalign',
                                                        'rawmode',
                                                    ]"
                                                ></simple-text-editor>
                                                <span class="validation-invalid-label">{{ errors[0] }}</span>
                                            </div>
                                        </ValidationProvider>
                                    </div>
                                </template>
                            </limitless-tabs>
                        </div>
                        <div class="card-footer text-right">
                            <limitless-button ref="btnsavecodetype" type="button" @click="saveCodeType" icon="icon-floppy-disk" :busy="busy_saving">Spara</limitless-button>
                        </div>
                    </div>
                </form>
            </ValidationObserver>
        </div>
        <div v-else-if="not_found" class="content">
            <i>Kodtypen hittades inte</i>
        </div>
    </div>
</template>

<script>
import apiClient from '@services/ApiClient';
import getError from '@services/ErrorHelper';
import BackendRepository from '@services/repositories/BackendRepository';
import LimitlessButton from '@components/Limitless/LimitlessButton';
import LimitlessPageHeader from '@components/Layout/partials/LimitlessPageHeader';
import LimitlessPageHeaderButton from '@components/Layout/partials/LimitlessPageHeaderButton';
import LimitlessTabs from '@components/Limitless/LimitlessTabs.vue';
import SimpleTextEditor from '@components/SimpleTextEditor.vue';

export default {
    name: 'ProductCodeTypeEdit',
    components: {
        LimitlessTabs,
        SimpleTextEditor,
        LimitlessButton,
        LimitlessPageHeader,
        LimitlessPageHeaderButton,
    },
    props: {
        id: {
            type: [Number, String],
            required: true,
        },
    },
    data: () => ({
        not_found: false,
        initialized: false,
        busy_loading: true,
        busy_saving: false,
        code_type: null,
        languages: [],
    }),
    mounted() {
        this.getLanguages();
        this.getCodeType();
    },
    watch: {
        id() {
            this.getCodeType();
        },
    },
    methods: {
        getCodeType() {
            this.busy_loading = true;
            this.not_found = false;
            apiClient.get('/vue/product_code_types/' + this.id)
                .then((response) => {
                    this.code_type = response.data;
                    this.ensureTranslations();
                })
                .catch((error) => {
                    const { status } = error.response;
                    if (status === 404) {
                        this.not_found = true;
                    }
                })
                .finally(() => {
                    this.initialized = true;
                    this.busy_loading = false;
                });
        },
        async saveCodeType() {
            const isValid = await this.$refs.codetypeform.validate();
            if (isValid) {
                this.busy_saving = true;
                await apiClient.post('/vue/product_code_types/' + this.id, {
                    translations: this.code_type.translations,
                })
                    .then((response) => {
                        this.code_type = response.data;
                        this.ensureTranslations();
                        this.$refs.btnsavecodetype.triggerSaved();
                    })
                    .catch((error) => {
                        const parsederror = getError(error);
                        if (typeof parsederror === 'object') {
                            this.$refs.codetypeform.setErrors(parsederror);
                        }
                        this.$refs.btnsavecodetype.triggerFailed();
                    })
                    .finally(() => {
                        this.busy_saving = false;
                    });
            } else {
                this.$refs.btnsavecodetype.triggerFailed();
            }
        },
        getLanguages() {
            BackendRepository.getLanguages().then((res) => {
                this.languages = res;
                this.ensureTranslations();
            });
        },
        ensureTranslations() {
            if (!this.code_type || !this.languages.length) {
                return;
            }
            if (!this.code_type.translations) {
                this.$set(this.code_type, 'translations', {});
            }

            this.languages.forEach((lang) => {
                if (!this.code_type.translations[lang.short]) {
                    this.$set(this.code_type.translations, lang.short, {
                        product_code_type_id: this.code_type.ProductCodeTypeID,
                        langcode: lang.short,
                        delivery_info: '',
                    });
                }
            });
        },
    },
    computed: {
        title() {
            return this.code_type ? this.code_type.ProductCodeTypeName : '';
        },
        title_id() {
            return this.code_type ? this.code_type.ProductCodeTypeID : '';
        },
        language_tabs() {
            if (!this.code_type) {
                return [];
            }

            return this.languages.map((lang) => ({
                id: lang.short,
                label: lang.name,
                slot: 'slot-' + lang.short,
            }));
        },
    },
};
</script>
